import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { BarsIcon, PinIcon } from "@/assets/icons";
import {
  CITIES,
  CITY_ORDER,
  CityKey,
  MAX_PICKS,
  Pick,
  SERVICES,
  SERVICE_ORDER,
  ServiceKey,
  TARIFF_LABEL,
  pickId,
} from "@/features/services";
import { useGeoCity } from "@/features/useGeoCity";
import { useProfileStore } from "@/store/profile";
import cls from "./styles.module.scss";

/*
 * Знакомство (A3–A8). Только клиент: собирает город, сервисы, тарифы и имя,
 * кладёт их в профиль и отдаёт человека на «Сравни с ИИ».
 */
const STEPS = ["splash", "geo", "city", "svc", "tariff", "name"] as const;
type Step = (typeof STEPS)[number];

export const OnboardingPage = () => {
  const navigate = useNavigate();
  const profile = useProfileStore();
  const [step, setStep] = useState<Step>("splash");
  const [city, setCity] = useState<CityKey | null>(null);
  const [services, setServices] = useState<ServiceKey[]>([]);
  const [picks, setPicks] = useState<Pick[]>([]);
  const [name, setName] = useState("");
  const geo = useGeoCity();

  useEffect(() => {
    if (step !== "splash") return undefined;
    const id = window.setTimeout(() => setStep("geo"), 1400);
    return () => window.clearTimeout(id);
  }, [step]);

  const idx = STEPS.indexOf(step);
  const dots = (
    <div className={cls.steps}>
      {STEPS.map((s, i) => (
        <i key={s} className={i <= idx ? cls.on : undefined} />
      ))}
    </div>
  );

  // Разрешил — город подставился, идём дальше; запретил или не вышло — выбирает сам.
  const allowGeo = async () => {
    const r = await geo.detect();
    if (r) {
      setCity(r.city);
      setStep("svc");
    } else {
      setStep("city");
    }
  };

  const toggleService = (k: ServiceKey) =>
    setServices((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const togglePick = (p: Pick) =>
    setPicks((s) => {
      const has = s.some((x) => pickId(x) === pickId(p));
      if (has) return s.filter((x) => pickId(x) !== pickId(p));
      return s.length >= MAX_PICKS ? s : [...s, p];
    });

  const finish = () => {
    if (city) profile.setCity(city);
    if (picks.length) profile.setPicks(picks);
    profile.setName(name.trim() || null);
    profile.setOnboardingDone(true);
    navigate("/");
  };

  if (step === "splash") {
    return (
      <div className={cls.page}>
        <div className={classNames(cls.onb, cls.splash)}>
          <span className={cls.welcomeIco}>
            <BarsIcon />
          </span>
          <h2>
            Считаем, где сейчас
            <br />
            выгоднее выходить
          </h2>
          <p className={cls.sub}>Сравниваем сервисы и тарифы по вашему городу и вашим часам.</p>
          <div className={cls.loadbar}>
            <i />
          </div>
          <div className={cls.hint}>Загружаем данные по сервисам…</div>
        </div>
      </div>
    );
  }

  if (step === "geo") {
    return (
      <div className={cls.page}>
        <div className={cls.onb}>
          {dots}
          <h2>Откуда вы?</h2>
          <p className={cls.sub}>
            Ставки и спрос сильно различаются по городам. Нужен город, чтобы сразу показать доход
            и спрос в нём. Можно ввести вручную.
          </p>
          <div className={cls.foot}>
            <button
              type="button"
              className={classNames(cls.btn, cls.primary)}
              disabled={geo.status === "busy"}
              onClick={allowGeo}
            >
              <PinIcon />
              {geo.status === "busy" ? "Определяем…" : "Определить по геопозиции"}
            </button>
            <button type="button" className={classNames(cls.btn, cls.text)} onClick={() => setStep("city")}>
              Выбрать вручную
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "city") {
    return (
      <div className={cls.page}>
        <div className={cls.onb}>
          {dots}
          <h2>Ваш город</h2>
          <p className={cls.sub}>
            {geo.status === "error" && geo.error
              ? `${geo.error}. Выберите город вручную.`
              : "Пока считаем по трём городам — остальные добавим по мере данных."}
          </p>
          <div className={cls.hits}>
            {CITY_ORDER.map((k) => (
              <button
                key={k}
                type="button"
                className={classNames(cls.hit, city === k && cls.hitOn)}
                onClick={() => setCity(k)}
              >
                {CITIES[k].name}
                {city === k && <span>выбран</span>}
              </button>
            ))}
          </div>
          <div className={cls.foot}>
            <button
              type="button"
              className={classNames(cls.btn, cls.primary)}
              disabled={!city}
              onClick={() => setStep("svc")}
            >
              Далее
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "svc") {
    return (
      <div className={cls.page}>
        <div className={cls.onb}>
          {dots}
          <h2>В каких сервисах работаете?</h2>
          <p className={cls.sub}>Отметьте те, где реально выходите. Тарифы выберем на следующем шаге.</p>
          <div className={cls.hits}>
            {SERVICE_ORDER.map((k) => (
              <button
                key={k}
                type="button"
                className={classNames(cls.hit, cls.hitLogo, services.includes(k) && cls.hitOn)}
                onClick={() => toggleService(k)}
              >
                <span className={cls.lg}>
                  <img src={SERVICES[k].logo} alt="" />
                </span>
                {SERVICES[k].name}
                {services.includes(k) && <span>выбран</span>}
              </button>
            ))}
          </div>
          <div className={cls.foot}>
            <button
              type="button"
              className={classNames(cls.btn, cls.primary)}
              disabled={!services.length}
              onClick={() => setStep("tariff")}
            >
              Далее
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "tariff") {
    return (
      <div className={cls.page}>
        <div className={cls.onb}>
          {dots}
          <h2>На каких тарифах?</h2>
          <p className={cls.sub}>До четырёх — именно они окажутся на графике.</p>
          {services.map((k) => (
            <div key={k} className={cls.grp}>
              <div className={cls.grpHead}>{SERVICES[k].name}</div>
              {SERVICES[k].tariffs.map((t) => {
                const p = { k, t };
                const on = picks.some((x) => pickId(x) === pickId(p));
                return (
                  <button
                    key={t}
                    type="button"
                    className={classNames(cls.hit, on && cls.hitOn)}
                    onClick={() => togglePick(p)}
                  >
                    {TARIFF_LABEL[t]}
                    {on && <span>выбран</span>}
                  </button>
                );
              })}
            </div>
          ))}
          <div className={cls.foot}>
            <button
              type="button"
              className={classNames(cls.btn, cls.primary)}
              disabled={!picks.length}
              onClick={() => setStep("name")}
            >
              Далее
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.onb}>
        {dots}
        <h2>Как к вам обращаться?</h2>
        <p className={cls.sub}>Помощник будет звать по имени. Можно пропустить.</p>
        <input
          className={cls.fld}
          placeholder="Имя"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") finish();
          }}
        />
        <div className={cls.foot}>
          <button type="button" className={classNames(cls.btn, cls.primary)} onClick={finish}>
            Готово
          </button>
          <button type="button" className={classNames(cls.btn, cls.text)} onClick={finish}>
            Пропустить
          </button>
        </div>
      </div>
    </div>
  );
};
